import { Box, IconButton, TableCell } from "@mui/material";
import * as React from "react";
import { useLocation, useNavigate } from "react-router";

import { FormattedDateTime } from "@/shared/datetime";
import { AssociationType } from "@/shared/domain/association";
import { ContentCopyIcon } from "@/shared/foundation/icons/mui";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { Goto } from "@/shared/navigation";
import { Column, RowCell } from "@/types";

import { DeviceLink } from "../DeviceLink";
import { RobotoLinkHoverUnderline } from "../RobotoLinkHoverUnderline";
import { RobotoTooltip } from "../RobotoTooltip";
import { UserLink } from "../UserLink";

const cellSpanStyle: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  userSelect: "none",
  fontWeight: 400, // You can use a number here
};

const Cell: React.FC<{ column: Column; cell: RowCell }> = ({
  column,
  cell,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  switch (cell.type) {
    case "chips":
      if (!Array.isArray(cell.data)) {
        throw new Error("Expect 'chips' cells to have a string[] data type.");
      }
      return (
        // A click on a tag searches by it, and stops here rather than also
        // selecting the row.
        <span onClick={(event) => event.stopPropagation()}>
          <TagGroup
            maxDisplayCount={3}
            tags={cell.data.sort()}
            onClick={(tag: string) => {
              const queryParams = new URLSearchParams();
              queryParams.set("query", tag);
              void navigate(`${pathname}?${queryParams.toString()}`);
            }}
          />
        </span>
      );
    case "datetime":
      if (typeof cell.data !== "string") {
        throw new Error("Expect 'datetime' cells to have a string data type.");
      }

      return (
        <span style={cellSpanStyle}>
          <FormattedDateTime value={cell.data} timeZone />
        </span>
      );
    case "id": {
      if (typeof cell.data !== "string") {
        throw new Error("Expect 'id' cells to have a string data type.");
      }

      // A cell in a `dataset_id`, `association_id` or `file_id` column links to
      // the page of the entity it names. Any other id cell (a file's path, a
      // topic's ID) links to the file or dataset its `association` names, and
      // to nothing when it has no association or one of another type.
      let cellLink = null;

      if (column.title.includes("dataset_id")) {
        cellLink = Goto.createDatasetRoute(cell.data);
      } else if (column.title.includes("association_id")) {
        cellLink = Goto.createAssociationRoute(cell.data);
      } else if (column.title.includes("file_id")) {
        cellLink = Goto.createFileRoute(cell.data);
      } else if (cell.association?.association_type === AssociationType.File) {
        cellLink = Goto.createFileRoute(cell.association.association_id);
      } else if (
        cell.association?.association_type === AssociationType.Dataset
      ) {
        cellLink = Goto.createDatasetRoute(cell.association.association_id);
      }

      return (
        <span style={cellSpanStyle}>
          {cellLink ? (
            <RobotoLinkHoverUnderline to={cellLink}>
              {cell.data}
            </RobotoLinkHoverUnderline>
          ) : (
            <span style={{ cursor: "pointer" }}>{cell.data}</span>
          )}
        </span>
      );
    }
    case "email":
      if (typeof cell.data !== "string") {
        throw new Error("Expect 'email' cells to have a string data type.");
      }
      return (
        <span style={cellSpanStyle}>
          <UserLink userId={cell.data} />
        </span>
      );
    case "device":
      if (typeof cell.data !== "string") {
        throw new Error("Expect 'device' cells to have a string data type.");
      }
      return (
        <span style={cellSpanStyle}>
          <DeviceLink deviceId={cell.data} />
        </span>
      );
    case "integration":
      if (typeof cell.data !== "string") {
        throw new Error(
          "Expect 'integration' cells to have a string data type.",
        );
      }

      if (cell.data === "foxglove-import") {
        return (
          <span style={cellSpanStyle}>
            <RobotoLinkHoverUnderline
              to={Goto.route.Settings.Integrations.Foxglove}
            >
              foxglove-import
            </RobotoLinkHoverUnderline>
          </span>
        );
      } else {
        return <span style={cellSpanStyle}>{cell.data}</span>;
      }
    case "invocation":
      if (typeof cell.data !== "string") {
        throw new Error(
          "Expect 'invocation' cells to have a string data type.",
        );
      }
      return (
        <RobotoLinkHoverUnderline
          variant="body2"
          to={Goto.createInvocationRoute(cell.data)}
        >
          {cell.data}
        </RobotoLinkHoverUnderline>
      );
    default:
      return <span style={cellSpanStyle}>{cell.data}</span>;
  }
};

const CopyButton: React.FC<{ cell: RowCell; hovering: boolean }> = ({
  cell,
  hovering,
}) => {
  const [labelText, setLabelText] = React.useState<string>("Copy");

  const cellIsEmpty =
    (typeof cell.data === "string" || Array.isArray(cell.data)) &&
    cell.data.length === 0;
  if (cellIsEmpty) {
    return null;
  }

  return (
    <Box
      sx={{
        opacity: hovering ? 1 : 0,
        transition: "opacity 0.2s",
        display: "inline",
      }}
    >
      <RobotoTooltip title={labelText}>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            navigator.clipboard
              .writeText(cell.data.toString())
              .then(() => {
                setLabelText("Copied!");
              })
              .catch(() => {
                setLabelText("Failed to copy.");
              });
          }}
          onBlur={() => {
            setLabelText("Copy");
          }}
          sx={{
            ml: "2px",
            color: "#7d8fb361",
            ":hover": {
              color: "#7D8FB3",
            },
          }}
        >
          <ContentCopyIcon
            sx={{
              fontSize: "12px",
            }}
          />
        </IconButton>
      </RobotoTooltip>
    </Box>
  );
};

interface DataGridCellProps {
  column: Column;
  constantHeight: number;
  cell: RowCell;
}

export const DataGridCell: React.FC<DataGridCellProps> = ({
  column,
  constantHeight,
  cell,
}) => {
  const [hovering, setHovering] = React.useState<boolean>(false);

  const padding = 8;
  return (
    <TableCell
      component="td"
      scope="row"
      sx={{
        border: 0,
        minWidth: column.width + "px",
        maxWidth: column.width + "px",
        width: column.width + "px",
        height: constantHeight + "px",
        padding: "0px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        textAlign: "left",
      }}
    >
      <Box
        sx={{
          height: constantHeight + "px",
          lineHeight: constantHeight - padding * 2 + "px",
          padding: padding + `px 8px`,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textAlign: "left",
          color: "rgb(107, 114, 128)",
          fontSize: ".85rem",
          fontStyle: "normal",
          display: "flex",
          alignItems: "center",
        }}
        onMouseOver={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <Cell column={column} cell={cell} />
        <CopyButton cell={cell} hovering={hovering} />
      </Box>
    </TableCell>
  );
};
